import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TypedDateInput } from "@/components/TypedDateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

export const ProjectCommunicationDrawer = ({ item, onClose, onSaved, project }) => {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    title: item?.title || "",
    notes: item?.notes || "",
    additional_notes: item?.additional_notes || "",
    item_type: item?.item_type || "note",
    visibility: item?.visibility || "shared",
    visible_to_user_ids: item?.visible_to_user_ids || [],
    priority: item?.priority || "medium",
    due_at: item?.due_at || "",
    assigned_user_ids: item?.assigned_user_ids || [],
  });
  const isHistory = Boolean(item?.deleted_at) || item?.status === "complete";

  useEffect(() => {
    axios.get(`${API}/assignable-users`).then((response) => setUsers(response.data.users || []));
  }, []);

  const save = async () => {
    setError("");
    try {
      const payload = { ...form, project_id: project.id };
      if (item?.id && item.visibility_version === "stage2") {
        await axios.patch(`${API}/communication-items/${item.id}`, {
          project_id: project.id,
          visibility: form.visibility === "shared" ? "company" : form.visibility,
          visible_to_user_ids: form.visible_to_user_ids,
        });
      } else if (item?.id) {
        await axios.patch(`${API}/communication-items/${item.id}`, payload);
      } else {
        await axios.post(`${API}/communication-items`, payload);
      }
      await onSaved();
      onClose();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Communication item could not save.");
    }
  };

  const convertToTask = async () => {
    setError("");
    try {
      await axios.post(`${API}/communication-items/${item.id}/convert-to-task`, {
      });
      await onSaved();
      onClose();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Communication could not become a Task.");
    }
  };

  const runAction = async (path) => {
    setError("");
    try {
      await axios.post(
        `${API}/communication-items/${item.id}/${path}`,
        path === "resolve" ? { outcome: "completed", note: "" } : {},
      );
      await onSaved();
      onClose();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Communication action could not save.");
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="project-communication-drawer">
        <DialogHeader>
          <DialogTitle>{item ? "Edit communication" : "New communication"}</DialogTitle>
          <DialogDescription>
            This communication remains linked to the Project shown below.
          </DialogDescription>
        </DialogHeader>
        <p data-testid="project-communication-fixed-project">
          {project.project_code} · {project.name}
        </p>
        {error && <p data-testid="project-communication-error">{error}</p>}
        <input
          data-testid="project-communication-title"
          disabled={Boolean(item) || isHistory}
          onChange={(event) => setForm({ ...form, title: event.target.value })}
          value={form.title}
        />
        <textarea
          data-testid="project-communication-notes"
          disabled={Boolean(item) || isHistory}
          onChange={(event) => setForm({ ...form, notes: event.target.value })}
          value={form.notes}
        />
        {!item && (
          <textarea
            data-testid="project-communication-additional-notes"
            disabled={isHistory}
            onChange={(event) => setForm({ ...form, additional_notes: event.target.value })}
            placeholder="Additional Notes (optional)"
            value={form.additional_notes}
          />
        )}
        {form.item_type === "task" && (
          <>
            <select
              data-testid="project-communication-priority"
              disabled={isHistory}
              onChange={(event) => setForm({ ...form, priority: event.target.value })}
              value={form.priority}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="urgent">Urgent</option>
            </select>
            <TypedDateInput
              data-testid="project-communication-due"
              disabled={isHistory}
              onChange={(value) => setForm({ ...form, due_at: value })}
              value={form.due_at}
            />
          </>
        )}
        <select
          data-testid="project-communication-visibility"
          disabled={isHistory}
          onChange={(event) => setForm({
            ...form,
            visibility: event.target.value,
            assigned_user_ids: [],
          })}
          value={form.visibility}
        >
          <option value="shared">Shared</option>
          <option value="personal">Private</option>
          {form.visibility === "selected_people" && (
            <option value="selected_people">Selected people</option>
          )}
        </select>
        {form.visibility === "shared" && users.map((user) => (
          <label key={user.id}>
            <input
              checked={form.assigned_user_ids.includes(user.id)}
              disabled={isHistory}
              onChange={(event) => setForm({
                ...form,
                assigned_user_ids: event.target.checked
                  ? [...form.assigned_user_ids, user.id]
                  : form.assigned_user_ids.filter((id) => id !== user.id),
              })}
              type="checkbox"
            />
            {user.name}
          </label>
        ))}
        <button
          data-testid="project-communication-save"
          disabled={isHistory}
          onClick={save}
          type="button"
        >
          Save
        </button>
        {!isHistory && item?.item_type === "note" && (
          <button
            data-testid="project-communication-convert-task"
            onClick={convertToTask}
            type="button"
          >
            Make Task
          </button>
        )}
        {!isHistory && item?.item_type === "task" && item.status === "open" && (
          <button
            data-testid="project-communication-complete"
            onClick={() => runAction("complete")}
            type="button"
          >
            Complete
          </button>
        )}
        {!isHistory && item?.item_type === "task" && item.status === "open" && (
          <button
            data-testid="project-communication-resolve"
            onClick={() => runAction("resolve")}
            type="button"
          >
            Resolve
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
};